import React, { useState, useEffect, useContext, Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import { setLoadedProducts } from './global-state';
import * as Basket from './page-basket';
import { BasketContext } from './context-basket';
import * as Product from './product';
import { searchClient } from './algolia';
import {
  Configure,
  RefinementList,
  Highlight,
  ClearRefinements,
  useStats,
  Hits,
  InstantSearch,
  SearchBox,
  useInstantSearch,
  useHierarchicalMenu,
  useSearchBox,
} from 'react-instantsearch';
import {
  Link,
  ScrollRestoration,
  useLoaderData,
  useSearchParams,
} from 'react-router-dom';

import type { Hit } from 'instantsearch.js';
import {
  countFormat,
  Footer,
  Logo,
  PillButton,
  AppHeader,
  pillButtonClassName,
} from './template';
import { Pagination } from './pagination';
import {
  CategoryNav,
  CATEGORY_ATTRIBUTES,
  CATEGORY_SEPARATOR,
} from './category-nav';
import {
  slugPathToURL,
  slugPathToTitles,
  categoryTitle,
  categoryPathLabel,
  useTaxonomy,
  withQuery,
} from './category-routes';
import * as Algolia from './algolia';
import { history } from 'instantsearch.js/es/lib/routers';
import { ScanButton } from './scanner/scan-button';

import './index.css';

import iconFilter from 'url:./assets/icon-filter.svg';
import iconBasket from 'url:./assets/icon-basket.svg';

import noPhoto from 'url:./assets/no-photo.png';
import noResultsImage from 'url:./assets/no-results.svg';

function ProductCache() {
  const { results } = useInstantSearch();
  useEffect(() => {
    setLoadedProducts(results.hits);
    return () => {};
  }, [results]);
  return null;
}

function BasketButton({ basket }) {
  const itemCount = basket.products
    ? Basket.sumItemCount(Object.values(basket.products))
    : 0;

  return (
    <Link to="/basket">
      <div className="bg-ui hover:bg-green-500 rounded-xl text-white w-[5.5rem]">
        <div className="flex items-center justify-center">
          <img src={iconBasket} className="w-10 h-10 p-1 mr-1" />
          <div className="w-7">{itemCount}</div>
        </div>
      </div>
    </Link>
  );
}

// A category can resolve fine in the taxonomy (it's a real node) while
// carrying zero products in the index -- e.g. a leaf that was curated but
// never actually populated, or one that lost its last product. InstantSearch
// silently DROPS a hierarchicalMenu refinement whose value doesn't exist as
// a facet value at all, which makes the page fall back to the entire
// unfiltered catalog instead of showing "no results" -- i.e. every dead
// category renders identical duplicate content. This is a single
// `hitsPerPage: 0` probe (no hits transferred, just `nbHits`) for the exact
// slug path this page resolved to, so the loader/page can tell "zero
// products" apart from "InstantSearch about to mask zero products as
// everything".
async function isCategoryEmpty(slugs: string[]): Promise<boolean> {
  try {
    const attribute = CATEGORY_ATTRIBUTES[slugs.length - 1];
    const value = slugs.join(CATEGORY_SEPARATOR);
    const { nbHits } = await Algolia.products.search('', {
      hitsPerPage: 0,
      filters: 'searchable:true',
      facetFilters: [[`${attribute}:${value}`]],
    });
    return nbHits === 0;
  } catch (e) {
    // Fail OPEN: a flaky probe (network blip) should fall back to the
    // pre-fix behaviour (unfiltered results) rather than block the whole
    // category page load.
    console.error('category emptiness probe failed', e);
    return false;
  }
}

// Resolves the `/category/*` splat against the taxonomy (fetched here, in
// the loader -- slugPathToTitles() is async now that the taxonomy is a
// runtime fetch, not a bundled import) and 404s on unknown slugs before the
// page ever renders, same as ClusterPage's/ProductPage's loaders.
export async function loader({ params }) {
  const slugs = (params['*'] || '').split('/').filter(Boolean);
  const titles = await slugPathToTitles(slugs);

  if (!titles) {
    throw new Response('Category not found', {
      status: 404,
      statusText: 'Not Found',
    });
  }

  const empty = await isCategoryEmpty(slugs);

  return { slugs, titles, empty };
}

// Renders SearchPage pre-refined to the `/category/*` path resolved by
// `loader` above -- unless the loader's probe found zero products for this
// exact path, in which case EmptyCategoryPage renders instead (see there
// for why: mounting SearchPage/InstantSearch at all would show the
// unfiltered catalog, not "no results").
export function CategoryPage() {
  const { slugs, titles, empty } = useLoaderData();
  // Keyed by the category path: navigating between two /category/* URLs
  // re-renders the SAME mounted route element, and InstantSearch's
  // initialUiState is initial-only -- without the remount, the URL
  // changes but the refinement (and results) silently don't.
  if (empty) {
    return (
      <EmptyCategoryPage
        key={slugs.join('/')}
        categorySlugs={slugs}
        categoryTitles={titles}
      />
    );
  }
  return (
    <SearchPage
      key={slugs.join('/')}
      categorySlugs={slugs}
      categoryTitles={titles}
    />
  );
}

// Shown instead of SearchPage for a category that resolves in the taxonomy
// but currently has no products (see isCategoryEmpty in loader above).
// Deliberately does NOT mount <InstantSearch>/<SearchPage> -- that's the
// whole bug this fixes, since InstantSearch drops a refinement with no
// matching facet value and silently shows the entire unfiltered catalog
// instead. This is a plain, static page shell instead: same header/footer
// as the detail pages (ClusterPage, ProductPage), a breadcrumb built from
// the already-resolved titles/slugs, an honest empty-state message, and a
// link back to a live ancestor (the parent category, or /search for a
// top-level one).
function EmptyCategoryPage({
  categorySlugs,
  categoryTitles,
}: {
  categorySlugs: string[];
  categoryTitles: string[];
}) {
  const canonical = `https://www.zuper-market.gr${slugPathToURL(categorySlugs)}`;
  const hasParent = categorySlugs.length > 1;
  const parentTo = hasParent
    ? slugPathToURL(categorySlugs.slice(0, -1))
    : '/search';
  const parentLabel = hasParent
    ? categoryTitles[categoryTitles.length - 2]
    : 'Όλα τα προϊόντα';

  return (
    <div>
      <Helmet>
        <title>{`Τιμές σε ${categoryTitle(categoryTitles)} — σύγκριση σούπερ μάρκετ | zuper`}</title>
        <meta
          name="description"
          content={`Σύγκρινε τιμές για ${categoryPathLabel(categoryTitles)} σε πολλαπλά σούπερ μάρκετ, σε πραγματικό χρόνο, στο zuper.`}
        />
        <link rel="canonical" href={canonical} />
        {/* This exact path resolves in the taxonomy but has zero products
            right now -- keep it out of the index as empty/duplicate
            content, while still letting crawlers follow the link below to
            a live ancestor. */}
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="pb-4 justify-center flex lg:flex hidden">
        <Logo size="4xl" />
      </div>

      <AppHeader />

      <div className="max-w-3xl mx-auto p-8">
        <nav
          aria-label="Κατηγορία"
          className="flex flex-wrap items-center gap-x-1.5 gap-y-1 pb-2 text-sm sm:text-base"
        >
          <Link
            to="/search"
            className="font-medium text-zuper-700 hover:text-zuper-900 hover:underline"
          >
            Όλες οι κατηγορίες
          </Link>
          {categoryTitles.map((title, index) => (
            <Fragment key={index}>
              <span className="text-gray-400">›</span>
              <Link
                to={slugPathToURL(categorySlugs.slice(0, index + 1))}
                className="font-medium text-zuper-700 hover:text-zuper-900 hover:underline"
              >
                {title}
              </Link>
            </Fragment>
          ))}
        </nav>

        <h1 className="text-xl font-semibold pt-4">
          {categoryTitle(categoryTitles)}
        </h1>

        <p className="text-gray-600 pt-4">
          Δεν υπάρχουν προϊόντα σε αυτή την κατηγορία αυτή τη στιγμή.
        </p>

        <div className="pt-6 flex">
          <Link
            to={parentTo}
            className={pillButtonClassName({ variant: 'grey' })}
          >
            {hasParent ? `Δες ${parentLabel}` : parentLabel}
          </Link>
        </div>

        <div className="pt-32">
          <Footer />
        </div>
      </div>
    </div>
  );
}

export function SearchPage({
  categorySlugs,
  categoryTitles,
}: {
  categorySlugs?: string[];
  categoryTitles?: string[];
} = {}) {
  const basket = useContext(BasketContext);
  const [searchParams] = useSearchParams();
  const [areMobileFiltersVisible, setMobileFiltersVisible] = useState(false);
  const sortingState = useState('price-per-unit-asc');
  const [sorting, setSorting] = sortingState;

  const toggleMobileFilters = () => {
    setMobileFiltersVisible(!areMobileFiltersVisible);
  };

  // Get initial query from URL parameters. The InstantSearch routing param
  // key follows the (sort-specific) index name, e.g. `PRODUCTS[query]` or
  // `staging_PRODUCTS[query]` on staging builds.
  const initialQuery =
    searchParams.get(`${Algolia.indexesBySorting[sorting]}[query]`) ||
    searchParams.get('q') ||
    '';

  const initialIndexUiState: { query: string; hierarchicalMenu?: object } = {
    query: initialQuery,
  };
  if (categorySlugs) {
    // URL slugs ARE facet segments now, so the refinement is built
    // straight from the URL -- no taxonomy lookup needed here.
    initialIndexUiState.hierarchicalMenu = {
      [CATEGORY_ATTRIBUTES[0]]: categorySlugs,
    };
  }

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={Algolia.indexesBySorting[sorting]}
      // Not `routing={true}`: that default (instantsearch.js's `history()`
      // router with no options) does its OWN `window.history.pushState`
      // ~400ms (writeDelay) after every uiState change, on top of the
      // react-router `Link` navigation (chip clicks, /search<->/category)
      // that already pushed a path entry. Two routers racing to write
      // history per click means Back has to walk through near-duplicate
      // intermediate states instead of one entry per category level -- and
      // the keyed remount above (see CategoryPage) means a NEW InstantSearch
      // instance re-writes params again right after mounting, compounding
      // it. `push` here (an instantsearch.js history-router option -- see
      // node_modules/instantsearch.js/es/lib/routers/history.js) swaps that
      // pushState for replaceState, so InstantSearch's router never creates
      // a history entry of its own; only react-router's Link navigations
      // do. Back/Forward then walk exactly the /category levels (and
      // /search). Everything else about routing (param format, stateMapping)
      // is left at its default, matching the previous `routing={true}`.
      routing={{
        router: history({
          push: (url) => {
            window.history.replaceState(window.history.state, '', url);
          },
        }),
      }}
      insights
      initialUiState={{
        [Algolia.indexesBySorting[sorting]]: initialIndexUiState,
      }}
    >
      <Helmet>
        {categorySlugs ? (
          <>
            <title>{`Τιμές σε ${categoryTitle(categoryTitles)} — σύγκριση σούπερ μάρκετ | zuper`}</title>
            <meta
              name="description"
              content={`Σύγκρινε τιμές για ${categoryPathLabel(categoryTitles)} σε πολλαπλά σούπερ μάρκετ, σε πραγματικό χρόνο, στο zuper.`}
            />
            <link
              rel="canonical"
              href={`https://www.zuper-market.gr${slugPathToURL(categorySlugs)}`}
            />
          </>
        ) : (
          <>
            <title>Αναζήτηση προϊόντων στα σούπερ μάρκετ | zuper</title>
            <meta
              name="description"
              content="Αναζήτησε προϊόντα σε ΑΒ, Σκλαβενίτη, Lidl, Μασούτη και άλλα σούπερ μάρκετ και σύγκρινε τιμές σε πραγματικό χρόνο."
            />
            {/* Parameter-soup search states shouldn't be indexed now that
                canonical /category pages exist for curated categories. */}
            <meta name="robots" content="noindex, follow" />
            <link rel="canonical" href="https://www.zuper-market.gr/search" />
          </>
        )}
      </Helmet>
      <Configure
        hitsPerPage={20}
        filters="searchable:true"
        attributesToRetrieve={[
          'id',
          'title',
          'slug',
          'kind',
          'market',
          'markets',
          'market-titles',
          'member-count',
          'prices',
          'photo-filename',
          'url',
          'collected-at',
        ]}
      />
      <ProductCache />
      <div>
        {/* Mobile toolbox */}
        <div className="lg:hidden text-center sticky top-0 z-40 shadow-md bg-white px-2 pt-1 pb-2">
          <div className="flex">
            <div className="w-32"></div>
            <div className="grow">
              <Logo size="2xl" />
            </div>
            <div className="w-32 flex justify-end">
              <BasketButton basket={basket} />
            </div>
          </div>
          <div className="pt-2 flex items-center gap-2">
            <ScanButton variant="compact" />
            <div className="flex-1">
              <MySearchBox />
            </div>
          </div>
        </div>

        {/* Mobile filter button */}
        <div
          onClick={toggleMobileFilters}
          className="lg:hidden border-gray-500 border-2 bg-white z-40 right-7 bottom-7 fixed shadow rounded-full filter-button-pulse"
        >
          <img
            src={iconFilter}
            className="w-10 h-10 p-2 opacity-50 hover:opacity-75"
          />
        </div>

        {/* Main content */}
        <div className="max-w-[126rem] p-2 xl:p-10 lg:p-5 grid grid-cols-8 mx-auto">
          <div className={`${areMobileFiltersVisible ? 'visible' : 'hidden'}`}>
            <MobileFilterPanel
              close={toggleMobileFilters}
              sortingState={sortingState}
            />
          </div>

          {/* Left panel */}
          <div
            id="logo"
            className="hidden lg:block lg:col-span-2 pr-4 justify-self-end"
          >
            <header>
              <h1 className="text-5xl text-lime-600 font-extrabold tracking-wide">
                <Link to="/">zuper</Link>
              </h1>
            </header>

            <div className="w-52 pt-5">
              <p className="text-gray-500 text-sm">
                Αναζήτηστε προϊόντα από πολλαπλά super market και συγκρίνετε
                εύκολα τιμές!
                <br />
                Έχετε απορίες;{' '}
                <Link to="/faq">
                  <span className="underline">Πατήστε εδώ</span>
                </Link>
                .
              </p>
            </div>

            <FilterPanel />
          </div>

          {/* Main panel */}
          <div className="col-span-8 lg:col-span-6 lg:space-y-4 space-y-2">
            <div className="hidden lg:block">
              <div className="flex space-x-2">
                <div className="grow">
                  <MySearchBox />
                </div>
                <BasketButton basket={basket} />
              </div>
            </div>

            <div className="lg:hidden mx-4 pb-4">
              <p className="text-gray-500 text-sm">
                Αναζήτηστε προϊόντα από πολλαπλά super market ταυτόχρονα και
                συγκρίνετε εύκολα τιμές! Έχετε απορίες;{' '}
                <Link to="/faq">
                  <span className="underline">Πατήστε εδώ</span>
                </Link>
                .
              </p>
            </div>

            <NoResultsBoundary fallback={<NoResults />}>
              <div className="pb-3">
                <CategoryNav />
              </div>

              <div className="pb-1 flex justify-center lg:hidden">
                <Pagination padding={2} />
              </div>

              <div className="pb-1 space-x-2 hidden lg:block lg:flex">
                <PillButton
                  onClick={() => setSorting('price-per-unit-asc')}
                  isSelected={sorting === 'price-per-unit-asc'}
                >
                  Αύξουσα τιμή κιλού/λίτρου
                </PillButton>
                <PillButton
                  onClick={() => setSorting('price-per-item-asc')}
                  isSelected={sorting === 'price-per-item-asc'}
                >
                  Αύξουσα τιμή τεμαχίου
                </PillButton>
                <div className="flex-grow" />
                <Pagination padding={2} />
              </div>

              <div className="sm:block hidden">
                <Hits
                  hitComponent={Product.DesktopHit}
                  classNames={{
                    list: 'grid 2xl:grid-cols-5 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-3 grid-cols-1',
                    item: 'm-1',
                  }}
                />
              </div>

              <div className="sm:hidden block">
                <Hits
                  hitComponent={Product.MobileHit}
                  classNames={{
                    list: 'grid grid-cols-1 divide-y',
                  }}
                />
              </div>

              <div className="flex justify-center">
                <Pagination
                  padding={2}
                  onNavigate={() => window.scrollTo(0, 0)}
                />
              </div>
            </NoResultsBoundary>

            <div className="pt-32">
              <Footer />
            </div>
          </div>
        </div>
        <ScrollRestoration />
      </div>
    </InstantSearch>
  );
}

function MobileFilterPanel({ close, sortingState }) {
  const { nbHits, processingTimeMS, query } = useStats();
  const [sorting, setSorting] = sortingState;
  const { results } = useInstantSearch();

  return (
    <div className="z-50 fixed right-0 left-0 top-0 bottom-0 bg-white overflow-y-scroll">
      <div className="text-center sticky top-0 shadow-md bg-white">
        <h1 className="text-md py-3">Φίλτρα</h1>
        <button onClick={close} className="top-3 left-1 px-4 fixed">
          x
        </button>
      </div>

      {!isNoResults(results) && (
        <div className="flex space-x-2 pt-4 justify-center">
          <PillButton
            onClick={() => setSorting('price-per-unit-asc')}
            isSelected={sorting === 'price-per-unit-asc'}
          >
            Αύξουσα τιμή κιλ./λιτ.
          </PillButton>

          <PillButton
            onClick={() => setSorting('price-per-item-asc')}
            isSelected={sorting === 'price-per-item-asc'}
          >
            Αύξουσα τιμή τεμ.
          </PillButton>
        </div>
      )}

      <div className="px-6">
        <FilterPanel />
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-1 bg-white shadow-[0_0_6px_rgba(0,0,0,0.25)] flex justify-center items-center">
        <button
          onClick={close}
          className="m-2 bg-ui py-3 rounded text-white text-sm w-full"
        >
          {countFormat.format(nbHits)} προϊόντα
        </button>
      </div>
    </div>
  );
}

// Top-level (`lvl0`) category list for the left facet panel. Uses the same
// attributes/separator as CategoryNav's chip row so both widgets refine the
// same underlying hierarchical facet -- react-instantsearch merges their
// uiState, so a refinement made here highlights the matching chip in
// CategoryNav (and vice versa). `items` from useHierarchicalMenu is always
// the top level of the tree regardless of the current refinement (unlike
// CategoryNav, which additionally walks into `item.data` to show the
// children of the refined path) -- children stay the chip row's job.
function CategoryFacetList() {
  const { query } = useSearchBox();
  const resolveLabel = useTaxonomy();
  // `items` here is always the top level of the tree (lvl0), so each row's
  // own slug path is just its own value (a single segment, no separator) --
  // no need to walk it like CategoryNav's itemSlugPath does.
  const { items } = useHierarchicalMenu({
    attributes: CATEGORY_ATTRIBUTES,
    separator: CATEGORY_SEPARATOR,
    limit: 20,
    sortBy: ['count:desc', 'name:asc'],
  });

  if (items.length === 0) return null;

  return (
    <ul>
      {items.map((item) => {
        const slugs = [item.value];
        // The selected row links to /search to clear the refinement
        // (mirrors the old checkbox-uncheck-to-clear behaviour); other rows
        // link into that top-level category.
        const to = item.isRefined
          ? withQuery('/search', query)
          : withQuery(slugPathToURL(slugs), query);

        return (
          <li key={item.value}>
            <Link
              to={to}
              className="min-w-full flex items-center hover:bg-gray-100 cursor-pointer py-1 pl-2 rounded-md"
            >
              <span
                aria-hidden="true"
                className={
                  'w-4 h-4 rounded border flex-none ' +
                  (item.isRefined
                    ? 'bg-lime-600 border-lime-600'
                    : 'border-gray-400')
                }
              />
              <span
                className={
                  'flex-grow text-sm ml-2 pr-2 ' +
                  (item.isRefined
                    ? 'text-black font-semibold'
                    : 'text-gray-600 hover:text-black')
                }
              >
                {resolveLabel(slugs)}
              </span>
              <span className="text-sm text-gray-400 before:content-['('] after:content-[')']">
                {countFormat.format(item.count)}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function FilterPanel() {
  const { results } = useInstantSearch();

  if (isNoResults(results)) return null;

  return (
    <div>
      <div className="pt-5 space-y-4">
        <h3 className="font-bold tracking-wide text-gray-600">Κατηγορίες</h3>
        <CategoryFacetList />

        <h3 className="font-bold tracking-wide text-gray-600">Super market</h3>
        <MyRefinementList attribute="market-titles" />

        <h3 className="font-bold tracking-wide text-gray-600">Κατασκευαστής</h3>
        <MyRefinementList attribute="manufacturer.name" uppercase={true} />
      </div>

      <div className="pt-12">
        <MyStats />
      </div>
    </div>
  );
}

function MySearchBox() {
  return (
    <SearchBox
      placeholder="Αναζήτηση προϊόντων..."
      searchAsYouType={false}
      autoFocus={true}
      classNames={{
        input:
          'rounded-full min-w-full border-gray-300 focus:border-lime-600 pl-10',
        submit: 'hidden',
        reset: 'hidden',
        loadingIndicator: 'hidden',
      }}
    />
  );
}

// source: https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-display/react/#handling-no-results

function isNoResults(results): boolean {
  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned.
  return !results.__isArtificial && results.nbHits === 0;
}

function NoResultsBoundary({ children, fallback }) {
  const { results } = useInstantSearch();

  if (isNoResults(results)) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return children;
}

function NoResults() {
  const { indexUiState } = useInstantSearch();

  return (
    <div className="justify-center">
      <p className="py-8 text-center text-lg">
        {indexUiState.query ? (
          <>
            <div className="flex justify-center">
              <img
                className="object-contain w-56 h-56"
                src={noResultsImage}
                alt="Empty basket image"
                title="Το άδειο καλάθι"
              />
            </div>
            <span class="text-gray-600">
              <p>Δεν βρέθηκαν προϊόντα για την αναζήτηση</p>
              <p>
                <q className="font-bold">{indexUiState.query}</q>
              </p>
            </span>
          </>
        ) : (
          <span>Δεν υπάρχουν προϊόντα.</span>
        )}
      </p>

      {indexUiState.query && (
        <div className="flex justify-center">
          <PillButton>
            <ClearRefinements
              excludedAttributes={[]}
              translations={{
                resetButtonText: 'Καθαρισμός αναζήτησης',
              }}
            />
          </PillButton>
        </div>
      )}
    </div>
  );
}

function caseInsensitiveCompare(str1, str2) {
  return str1.toLowerCase().localeCompare(str2.toLowerCase());
}

function facetCompare(x1, x2) {
  return caseInsensitiveCompare(x1.name, x2.name);
}

// react-instantsearch's <RefinementList> renders `item.count` verbatim (see
// its ui/RefinementList.js) with no template/render-prop hook to intercept
// it, so the Greek dot thousands-separator is applied here via
// `transformItems` -- it runs last, after sorting/limiting, and only feeds
// the render, so reformatting `count` into a display string doesn't affect
// refining/sorting/showMore logic.
function formatRefinementItems(items) {
  return items.map((item) => ({
    ...item,
    count: countFormat.format(item.count),
  }));
}

function MyRefinementList({ attribute, uppercase = false }) {
  return (
    <RefinementList
      attribute={attribute}
      sortBy={facetCompare} //this used to be an anonymous function used in-place, but it caused react into an infinite loop of rendering!
      transformItems={formatRefinementItems}
      showMore={true}
      showMoreLimit={30}
      classNames={{
        root: uppercase ? 'uppercase' : '',
        checkbox: 'rounded focus:ring-lime-600 text-lime-600 cursor-pointer',
        label:
          'min-w-full block hover:bg-gray-100 cursor-pointer py-1 pl-2 rounded-md',
        labelText: 'text-sm text-gray-600 ml-2 pr-2 hover:text-black',
        count:
          "text-sm text-gray-400 ml-2 before:content-['('] after:content-[')']",
        showMore:
          "text-sm text-gray-600 mt-4 mb-2 ml-2 font-medium after:content-['›'] after:ml-2 after:rotate-90",
        disabledShowMore: 'hidden',
      }}
      translations={{
        showMoreButtonText({ isShowingMore }) {
          return isShowingMore ? 'Δες λιγότερα' : 'Δες περισσότερα';
        },
      }}
    />
  );
}

function MyStats() {
  const { nbHits, processingTimeMS, query } = useStats();

  return (
    <div className="text-sm text-gray-500">
      Βρέθηκαν {countFormat.format(nbHits)} προιόντα σε {processingTimeMS}msec
    </div>
  );
}
